import {
  AGENT_RUN_LIMIT_BOUNDS,
  type AgentRunListItem,
  DEFAULT_AGENT_RUN_LIMITS,
  DEFAULT_BASE_BRANCH,
  normalizeRepoUrl,
} from '@fos/shared/contracts/agents';
import { DEFAULT_RUN_TOOLS, type McpToolName, RUN_TOKEN_TOOLS } from '@fos/shared/contracts/mcp';
import {
  DELEGATE_RUNTIME_LABELS,
  delegationReasonText,
  MCP_TOOL_LABELS,
  RUNNER_STATUS_LABELS,
} from '@fos/shared/labels';
import { ChevronDown } from 'lucide-react';
import { type FormEvent, useEffect, useId, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { errorMessage, validationIssues } from '../../../lib/api';
import { useActiveSession } from '../../../lib/auth';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import {
  Button,
  Checkbox,
  ContextBanner,
  Dialog,
  Field,
  FormMessage,
  Input,
  Select,
  type SelectOption,
  Textarea,
  toast,
} from '../../../ui';

/**
 * „Zleć Claude” (Fala 9, DECISIONS 2026-10-07 „Zmiany w kodzie i operacje robi tylko Claude”): zlecenie zmiany w kodzie
 * albo sprawy operacyjnej dla Claude — z karty zadania, incydentu monitoringu, błędu integracji (Ustawienia →
 * Integracje) i z odpowiedzi Copilota („Zmień…”). Instrukcja i kontekst wypełnione wstępnie, wykonawca z
 * `agents.delegationPreview`: lokalny runner (Claude Code na moim komputerze), gdy mam aktywny komputer
 * z repozytorium projektu, inaczej sesja Claude przez MCP (zlecenie czeka w kolejce, aż sesja je przyjmie). Dalej jak
 * dotąd: repozytorium, branch, limity, komenda testów i narzędzia MCP dla agenta. Usługa: `agents.create` (kanał `ui`).
 */
export interface DelegateTarget {
  /** zadanie wyznacza projekt i klienta (usługa) */
  taskId?: string | null;
  projectId?: string | null;
  clientId?: string | null;
  /** opis kontekstu pod tytułem okna, np. „#12 Formularz · REA” albo „Incydent: rea.pl” */
  contextLabel: string;
  /** nazwa projektu do podpowiedzi przy repozytorium */
  projectName?: string | null;
  /** tytuł zlecenia (bez zadania; przy zadaniu tytułem jest zadanie) */
  title?: string;
  /** instrukcja wypełniona wstępnie (incydent, błąd integracji, Copilot) */
  instruction?: string;
  /** wykonawca wskazany wcześniej (np. podgląd Copilota) — inaczej z `agents.delegationPreview` */
  runtime?: 'local_runner' | 'external';
  /** ostrzeżenie nad instrukcją (Copilot: instrukcja z rozmowy z danymi klienta — sprawdź ją przed zleceniem) */
  warning?: string;
}

type Runtime = 'local_runner' | 'external';

/**
 * Wykonawca: `local` = lokalny runner, pierwszy wolny z moich komputerów; `runner:<id>` = konkretny mój komputer
 * (`runnerId`); `external` = sesja Claude przez MCP (zlecenie w kolejce do przyjęcia przez `list_agent_runs`).
 */
type Executor = 'local' | 'external' | `runner:${string}`;

function runtimeOf(executor: Executor): Runtime {
  return executor === 'external' ? 'external' : 'local_runner';
}

interface FormState {
  title: string;
  instruction: string;
  executor: Executor;
  repoUrl: string;
  baseBranch: string;
  testCommand: string;
  maxMinutes: string;
  maxCostPln: string;
  maxTurns: string;
  tools: string[];
}

function initialState(target: DelegateTarget): FormState {
  return {
    title: target.title ?? '',
    instruction: target.instruction ?? '',
    executor: target.runtime === 'local_runner' ? 'local' : 'external',
    repoUrl: '',
    baseBranch: DEFAULT_BASE_BRANCH,
    testCommand: '',
    maxMinutes: String(DEFAULT_AGENT_RUN_LIMITS.maxMinutes),
    maxCostPln: DEFAULT_AGENT_RUN_LIMITS.maxCostPln.replace('.', ','),
    maxTurns: String(DEFAULT_AGENT_RUN_LIMITS.maxTurns),
    tools: [...DEFAULT_RUN_TOOLS],
  };
}

type Errors = Partial<Record<keyof FormState | 'form', string>>;

const PATH_TO_FIELD: Record<string, keyof FormState> = {
  title: 'title',
  instruction: 'instruction',
  runtime: 'executor',
  runnerId: 'executor',
  repoUrl: 'repoUrl',
  baseBranch: 'baseBranch',
  testCommand: 'testCommand',
  'limits.maxMinutes': 'maxMinutes',
  'limits.maxCostPln': 'maxCostPln',
  'limits.maxTurns': 'maxTurns',
  allowedTools: 'tools',
};

type RunnerOption = { id: string; name: string; status: 'online' | 'offline' | 'disabled' };

function executorOptions(runners: RunnerOption[]): SelectOption<Executor>[] {
  const options: SelectOption<Executor>[] = [
    {
      value: 'local',
      label:
        runners.length > 1
          ? `${DELEGATE_RUNTIME_LABELS.local_runner} — pierwszy wolny komputer`
          : DELEGATE_RUNTIME_LABELS.local_runner,
    },
  ];
  if (runners.length > 1) {
    for (const r of runners) {
      options.push({ value: `runner:${r.id}`, label: `${r.name} · ${RUNNER_STATUS_LABELS[r.status]}` });
    }
  }
  options.push({ value: 'external', label: DELEGATE_RUNTIME_LABELS.external });
  return options;
}

function executorNote(executor: Executor, runners: RunnerOption[], loaded: boolean): string {
  if (executor === 'external') {
    return 'Zlecenie czeka w kolejce (do 24 h), aż sesja Claude podłączona przez MCP je przyjmie. Odbiór i wdrożenie zostają po Twojej stronie.';
  }
  const chosen = executor.startsWith('runner:') ? runners.filter((r) => `runner:${r.id}` === executor) : runners;
  const base =
    'Runner na Twoim komputerze odbierze zlecenie, utworzy branch i odda wynik z testami. Nic nie trafi na produkcję bez Twojej zgody.';
  if (!loaded) return base;
  if (chosen.length === 0) {
    return 'Nie masz jeszcze komputera z runnerem — zlecenie poczeka w kolejce (do 24 h), aż runner się zgłosi.';
  }
  const online = chosen.filter((r) => r.status === 'online');
  if (online.length === 0) {
    const names = chosen.map((r) => r.name).join(', ');
    return `${names}: offline — zlecenie poczeka w kolejce (do 24 h), aż runner się zgłosi.`;
  }
  return `${online.map((r) => r.name).join(', ')}: online. ${base}`;
}

export function DelegateToClaudeDialog({
  target,
  open,
  onOpenChange,
  onCreated,
}: {
  target: DelegateTarget;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (run: AgentRunListItem) => void;
}) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const session = useActiveSession();
  const advancedId = useId();
  // moje komputery z runnerem (run odbiera tylko runner autora; wyłączone pomijamy)
  const runnersQuery = trpc.agents.listRunners.useQuery(undefined, { enabled: open, retry: false });
  const myRunners = (runnersQuery.data ?? []).filter((r) => r.user.id === session.user.id && r.status !== 'disabled');
  const context = {
    ...(target.taskId ? { taskId: target.taskId } : {}),
    ...(target.projectId && !target.taskId ? { projectId: target.projectId } : {}),
    ...(target.clientId && !target.taskId && !target.projectId ? { clientId: target.clientId } : {}),
  };
  // wykonawca domyślnie: lokalny runner przy aktywnym komputerze z repozytorium projektu, inaczej sesja Claude (MCP)
  const preview = trpc.agents.delegationPreview.useQuery(context, { enabled: open, retry: false });
  const [form, setForm] = useState<FormState>(() => initialState(target));
  const [executorTouched, setExecutorTouched] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [advanced, setAdvanced] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: stan formularza od nowa tylko przy otwarciu okna
  useEffect(() => {
    if (!open) return;
    setForm(initialState(target));
    setExecutorTouched(false);
    setErrors({});
    setAdvanced(false);
  }, [open]);

  const suggested = preview.data?.runtime;
  useEffect(() => {
    if (!open || executorTouched || target.runtime || !suggested) return;
    setForm((f) => ({ ...f, executor: suggested === 'local_runner' ? 'local' : 'external' }));
  }, [open, executorTouched, target.runtime, suggested]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const create = trpc.agents.create.useMutation({
    onSuccess: async (run) => {
      toast.success(
        run.runtime === 'local_runner'
          ? `Zlecono Claude — przebieg #${run.number} czeka na runner`
          : `Zlecono Claude — przebieg #${run.number} czeka na sesję Claude (MCP)`,
        { action: { label: 'Pokaż', onClick: () => navigate(`/agenci/${run.id}`) } },
      );
      await Promise.all([utils.agents.invalidate(), utils.tasks.invalidate()]);
      onOpenChange(false);
      onCreated?.(run);
    },
    onError: (err) => {
      const issues = validationIssues(err);
      const next: Errors = {};
      for (const i of issues) {
        const field = PATH_TO_FIELD[i.path] ?? PATH_TO_FIELD[i.path.split('.')[0] ?? ''];
        if (field && !next[field]) next[field] = i.message;
      }
      if (
        next.repoUrl ||
        next.baseBranch ||
        next.testCommand ||
        next.maxMinutes ||
        next.maxCostPln ||
        next.maxTurns ||
        next.tools
      ) {
        setAdvanced(true);
      }
      if (Object.keys(next).length === 0) next.form = errorMessage(err);
      setErrors(next);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: Errors = {};
    if (!form.instruction.trim()) next.instruction = 'Opisz, co Claude ma zrobić';
    if (form.repoUrl.trim() && normalizeRepoUrl(form.repoUrl) === null)
      next.repoUrl = 'Nieprawidłowy adres repozytorium';
    const minutes = Number(form.maxMinutes);
    const turns = Number(form.maxTurns);
    const cost = form.maxCostPln.replace(',', '.').trim();
    const { maxMinutes, maxTurns } = AGENT_RUN_LIMIT_BOUNDS;
    if (!Number.isInteger(minutes) || minutes < maxMinutes.min || minutes > maxMinutes.max) {
      next.maxMinutes = `Od ${maxMinutes.min} do ${maxMinutes.max} min`;
    }
    if (!Number.isInteger(turns) || turns < maxTurns.min || turns > maxTurns.max) {
      next.maxTurns = `Od ${maxTurns.min} do ${maxTurns.max}`;
    }
    if (!/^\d{1,3}(\.\d{1,2})?$/.test(cost) || Number(cost) <= 0) next.maxCostPln = 'Podaj kwotę, np. 10,00';
    if (Object.keys(next).length > 0) {
      if (next.repoUrl || next.maxMinutes || next.maxTurns || next.maxCostPln) setAdvanced(true);
      setErrors(next);
      return;
    }
    setErrors({});
    const runtime = runtimeOf(form.executor);
    create.mutate({
      ...context,
      ...(form.title.trim() && !target.taskId ? { title: form.title.trim() } : {}),
      instruction: form.instruction.trim(),
      runtime,
      // sesja Claude przez MCP: wykonawcą jest Claude Code (dziennik pokazuje „Agent · Claude Code”)
      ...(runtime === 'external' ? { provider: 'claude_code' } : {}),
      ...(form.executor.startsWith('runner:') ? { runnerId: form.executor.slice('runner:'.length) } : {}),
      ...(form.repoUrl.trim() ? { repoUrl: form.repoUrl.trim() } : {}),
      baseBranch: form.baseBranch.trim() || DEFAULT_BASE_BRANCH,
      ...(form.testCommand.trim() ? { testCommand: form.testCommand.trim() } : {}),
      allowedTools: form.tools,
      limits: { maxMinutes: minutes, maxCostPln: cost, maxTurns: turns },
    });
  };

  const toggleTool = (tool: string, on: boolean) =>
    set('tools', on ? [...form.tools, tool] : form.tools.filter((t) => t !== tool));

  const projectName = target.projectName ?? preview.data?.project?.name ?? null;
  const reason = preview.data ? delegationReasonText(preview.data) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} width="l" title="Zleć Claude" description={target.contextLabel}>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {target.warning && <ContextBanner tone="warning">{target.warning}</ContextBanner>}
        {!target.taskId && (
          <Field label="Tytuł zlecenia" hint="Puste = pierwsza linia instrukcji." error={errors.title}>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={200} />
          </Field>
        )}
        <Field
          label="Co Claude ma zrobić"
          hint={
            target.taskId
              ? 'Zadanie, opis i checklistę Claude dostanie samo (TASK.md, PROJECT.md).'
              : 'Opisz problem, oczekiwany wynik i ograniczenia. Hasła i klucze nie są potrzebne — Claude ich nie dostaje.'
          }
          error={errors.instruction}
        >
          <Textarea
            value={form.instruction}
            onChange={(e) => set('instruction', e.target.value)}
            rows={target.instruction ? 8 : 5}
            maxLength={20_000}
            placeholder="Np. Popraw walidację numeru telefonu w formularzu i dodaj test. Bez zmian w wyglądzie."
            autoFocus
          />
        </Field>
        <Field label="Wykonawca" error={errors.executor}>
          <Select<Executor>
            aria-label="Wykonawca"
            value={form.executor}
            onValueChange={(v) => {
              setExecutorTouched(true);
              set('executor', v);
            }}
            options={executorOptions(myRunners)}
          />
        </Field>
        <ContextBanner tone="info">
          {reason && !executorTouched && <>{reason} </>}
          {executorNote(form.executor, myRunners, runnersQuery.isSuccess)}
          {runtimeOf(form.executor) === 'local_runner' && runnersQuery.isSuccess && myRunners.length === 0 && (
            <>
              {' '}
              <Link
                to="/ustawienia/tokeny"
                className="font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
                onClick={() => onOpenChange(false)}
              >
                Dodaj komputer
              </Link>
            </>
          )}
        </ContextBanner>

        <button
          type="button"
          aria-expanded={advanced}
          aria-controls={advancedId}
          onClick={() => setAdvanced((v) => !v)}
          className="inline-flex w-fit items-center gap-1.5 rounded-xs text-body-s text-fg-secondary hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
        >
          <ChevronDown
            size={16}
            strokeWidth={1.75}
            aria-hidden
            className={cn('transition-transform', advanced ? 'rotate-0' : '-rotate-90')}
          />
          Repozytorium, limity i narzędzia
        </button>
        {advanced && (
          <div id={advancedId} className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-[1fr_140px]">
              <Field
                label="Repozytorium"
                hint={
                  projectName ? `Puste = repozytorium projektu „${projectName}”.` : 'Wymagane dla lokalnego runnera.'
                }
                error={errors.repoUrl}
              >
                <Input
                  value={form.repoUrl}
                  onChange={(e) => set('repoUrl', e.target.value)}
                  placeholder="https://github.com/fravity/…"
                  mono
                />
              </Field>
              <Field label="Branch bazowy" error={errors.baseBranch}>
                <Input value={form.baseBranch} onChange={(e) => set('baseBranch', e.target.value)} mono />
              </Field>
            </div>
            <Field
              label="Komenda testów"
              hint="Puste = z konfiguracji repozytorium w runnerze."
              error={errors.testCommand}
            >
              <Input
                value={form.testCommand}
                onChange={(e) => set('testCommand', e.target.value)}
                placeholder="npm test"
                mono
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Czas (min)" error={errors.maxMinutes}>
                <Input
                  value={form.maxMinutes}
                  onChange={(e) => set('maxMinutes', e.target.value)}
                  inputMode="numeric"
                  mono
                />
              </Field>
              <Field label="Koszt (zł)" error={errors.maxCostPln} hint="Tylko płatne API">
                <Input
                  value={form.maxCostPln}
                  onChange={(e) => set('maxCostPln', e.target.value)}
                  inputMode="decimal"
                  mono
                />
              </Field>
              <Field label="Kroki" error={errors.maxTurns}>
                <Input
                  value={form.maxTurns}
                  onChange={(e) => set('maxTurns', e.target.value)}
                  inputMode="numeric"
                  mono
                />
              </Field>
            </div>
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-label text-fg">Narzędzia Fravity OS dla agenta</legend>
              <div className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
                {RUN_TOKEN_TOOLS.map((tool) => (
                  <Checkbox
                    key={tool}
                    checked={form.tools.includes(tool)}
                    onCheckedChange={(on) => toggleTool(tool, on)}
                    label={MCP_TOOL_LABELS[tool as McpToolName]}
                  />
                ))}
              </div>
              {errors.tools ? (
                <p role="alert" className="text-caption text-danger">
                  {errors.tools}
                </p>
              ) : (
                <p className="text-caption text-fg-muted">
                  Agent widzi tylko ten projekt (bez sejfu). Wysyłka maili i zmiana pamięci zawsze wymagają akceptacji.
                </p>
              )}
            </fieldset>
          </div>
        )}

        {errors.form && <FormMessage>{errors.form}</FormMessage>}
        <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" loading={create.isPending}>
            Zleć Claude
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
